import React, { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { adminPrivilegedService, AdminActivityStats } from '@/services/admin/adminPrivilegedService';
import { ChefHat, Calendar, MessageSquare, Activity, UserPlus } from 'lucide-react';
import {
  ActivityMetricCard,
  ActivityTrendsChart,
  UserRegistrationsChart,
  ActivityStatsLoadingState,
  ActivityStatsErrorState
} from './activity';
import RecentActiveUsersTable from './RecentActiveUsersTable';

const AdminActivityStatsDisplay: React.FC = () => {
  const [stats, setStats] = useState<AdminActivityStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await adminPrivilegedService.getActivityStats();
        setStats(data);
      } catch (err) {
        console.error('Failed to load activity stats:', err);
        setError(err instanceof Error ? err.message : 'Failed to load activity statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <ActivityStatsLoadingState />;
  }

  if (error) {
    return <ActivityStatsErrorState error={error} />;
  }

  if (!stats) return null;

  const activityCards = [
    {
      title: 'Recipes Created',
      thisWeek: stats.thisWeek.recipesCreated,
      lastWeek: stats.lastWeek.recipesCreated,
      growth: stats.growthRates.recipesGrowth,
      icon: ChefHat,
      color: 'text-orange-600'
    },
    {
      title: 'Meals Planned',
      thisWeek: stats.thisWeek.mealsPlanned,
      lastWeek: stats.lastWeek.mealsPlanned,
      growth: stats.growthRates.mealsGrowth,
      icon: Calendar,
      color: 'text-purple-600'
    },
    {
      title: 'Feedback Added',
      thisWeek: stats.thisWeek.feedbackAdded,
      lastWeek: stats.lastWeek.feedbackAdded,
      growth: stats.growthRates.feedbackGrowth,
      icon: MessageSquare,
      color: 'text-blue-600'
    },
    {
      title: 'New Users',
      thisWeek: stats.thisWeek.newUsers,
      lastWeek: stats.lastWeek.newUsers,
      growth: stats.growthRates.newUsersGrowth,
      icon: UserPlus,
      color: 'text-emerald-600'
    },
    {
      title: 'Total Activity',
      thisWeek: stats.thisWeek.totalActivity,
      lastWeek: stats.lastWeek.totalActivity,
      growth: stats.growthRates.totalGrowth,
      icon: Activity,
      color: 'text-green-600'
    }
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold mt-2">Activity Statistics</h2>
        <Badge variant="default" className="bg-green-100 text-green-800">
          Live Data
        </Badge>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        {activityCards.map((card, index) => (
          <ActivityMetricCard
            key={index}
            title={card.title}
            thisWeek={card.thisWeek}
            lastWeek={card.lastWeek}
            growth={card.growth}
            icon={card.icon}
            color={card.color}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <ActivityTrendsChart weeklyTrends={stats.weeklyTrends} />
        <UserRegistrationsChart weeklyTrends={stats.weeklyTrends} />
      </div>

      <RecentActiveUsersTable users={stats.recentActiveUsers} />
    </div>
  );
};

export default AdminActivityStatsDisplay;
